import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template179: Excursiones SF - bloques separados (intro, sausalito, muirWoods, goldenGatePark, bigSur)
const Template179 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const contentColor = textColor || "#444";

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const getCanvasImageUrl = (id) =>
		page.canvasImages?.find((img) => img.id === id)?.imageUrl ?? "";
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const headerText = page.texts?.[0]?.content ?? "";
	const introText = page.texts?.[1]?.content ?? "";
	const logoImage = getCanvasImageUrl("logo");
	const sausalito = sections[0] ?? {};
	const muirWoods = sections[1] ?? {};
	const goldenGatePark = sections[2] ?? {};
	const bigSur = sections[3] ?? {};

	const box = {
		backgroundColor: "#fbf0cf",
		border: "2px solid #F2B705",
		borderRadius: "16px",
		padding: "16px",
	};
	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: 1,
		color: "#ca9904",
	};
	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};
	const ul = { paddingLeft: "18px", margin: 0 };

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100vh",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#F2B705",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
				{logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>
			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "12px 16px", fontSize: 13, color: contentColor }}>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}
			<Box data-block-id="text-2"
				sx={{
					padding: "0 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				{/* ——— Sausalito ——— */}
				{(sausalito.title || sausalito.items?.length) && (
					<Box data-block-id="text-3" sx={box}>
						{sausalito.title && (
							<Box component="h3" sx={{ ...h3, marginTop: 0 }}>
								{sausalito.title}
							</Box>
						)}
						{sausalito.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{sausalito.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
				{/* ——— Muir Woods ——— */}
				{(muirWoods.title || muirWoods.items?.length) && (
					<Box data-block-id="text-4" sx={{ ...box, position: "relative" }}>
						{muirWoods.title && (
							<Box component="h3" sx={{ ...h3, marginTop: 0 }}>
								{muirWoods.title}
							</Box>
						)}
						{muirWoods.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{muirWoods.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
						{getImageSrc(muirWoods.imageId) && (
							<Box
								component="img"
								src={getImageSrc(muirWoods.imageId)}
								alt=""
								sx={{
									width: isSmallScreen ? 80 : 100,
									position: "absolute",
									top: isSmallScreen ? -30 : 60,
									right: isSmallScreen ? -10 : 10,
									borderRadius: "12px",
									objectFit: "cover",
								}}
							/>
						)}
					</Box>
				)}
				{/* ——— Golden Gate Park ——— */}
				{(goldenGatePark.title || goldenGatePark.items?.length) && (
					<Box data-block-id="text-5" sx={box}>
						{goldenGatePark.title && (
							<Box component="h3" sx={{ ...h3, marginTop: 0 }}>
								{goldenGatePark.title}
							</Box>
						)}
						{goldenGatePark.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{goldenGatePark.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
				{/* ——— Big Sur ——— */}
				{(bigSur.title || bigSur.items?.length) && (
					<Box data-block-id="text-6" sx={box}>
						{bigSur.title && (
							<Box component="h3" sx={{ ...h3, marginTop: 0 }}>
								{bigSur.title}
							</Box>
						)}
						{bigSur.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{bigSur.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template179.thumbnail = "/images/template-1.png";
export default Template179;
